import { log, warn } from '@execonline-inc/logging';
import { minutes } from '@execonline-inc/time';
import { assertNever } from '@kofno/piper';
import { BadStatus } from 'ajaxian';
import { just, nothing } from 'maybeasy';
import { Task } from 'taskarian';
import { AppyError, callApi, request, retryOnSessionExpiration } from '../../Appy';
import { CurrentUserStore } from '../../CurrentUserStore';
import { currentUserResourceDecoder } from '../../CurrentUserStore/Decoders';
import { State, sessionExpiredMessage } from '../../CurrentUserStore/Types';
import { handleHttpError } from '../../ErrorHandling';
import { warnAndNotify } from '../../Honeybadger';
import { InactivityLogoutTracker } from '../../Inactivity/Core';
import { MissingLinkError, findLinkT } from '../../LinkyLinky';
import ReactionComponent, { RCProps } from '../../ReactionComponent';
import { Link } from '../../Resource/Types';
import { rootResourceStore } from '../../RootResourceStore';
import { RootResourceError } from '../../RootResourceStore/Types';
import { discardSession } from '../../Session';
import { sessionStore } from '../../Session/Store';

export const handleMissingLink = (store: CurrentUserStore) => (err: MissingLinkError) => {
  warnAndNotify(
    'MissingLinkError',
    `Client application did not receive link: ${err.rel}`,
    rootResourceStore.state,
  );
  store.setAnonymous(just('An error occurred, please refresh the page and try again'));
};

const handleBadStatusPerState = (store: CurrentUserStore) => {
  switch (store.currentUserState.kind) {
    case 'identifying':
      store.setAnonymous(just('Your request failed.'));
      break;
    case 'waiting':
    case 'identified':
    case 'anonymous':
    case 'unauthorized':
    case 'logging-out':
    case 'errored':
      break;
    default:
      assertNever(store.currentUserState);
  }
};

const handleBadStatusRest = (store: CurrentUserStore, err: BadStatus) => {
  switch (err.response.status) {
    case 423:
      store.setAnonymous(
        just('An email has been sent to you to verify your account and reset your password.'),
      );
      break;
    case 401:
      store.setAnonymous(just(sessionExpiredMessage));
      break;
    default:
      handleBadStatusPerState(store);
  }
};

const handleBadStatus = (store: CurrentUserStore, err: BadStatus) => {
  switch (store.currentUserState.kind) {
    case 'waiting':
      break;
    case 'identifying':
    case 'identified':
    case 'anonymous':
    case 'unauthorized':
    case 'logging-out':
    case 'errored':
      handleBadStatusRest(store, err);
      break;
    default:
      assertNever(store.currentUserState);
  }
};

const handleLoginFailure = (store: CurrentUserStore) => (err: RootResourceError) => {
  switch (err.kind) {
    case 'missing-link-error':
      handleMissingLink(store)(err);
      break;
    case 'bad-status':
      handleBadStatus(store, err);
      break;
    case 'bad-payload':
    case 'bad-url':
    case 'network-error':
    case 'timeout': {
      warn(handleHttpError(err));
      store.setErrored('Your request failed.');
      break;
    }
    case 'missing-application-id':
      store.setErrored('You are missing the application id');
      break;
    case 'missing-api-compatibility':
      store.setErrored('You are missing the API compatibility version');
      break;
    default:
      assertNever(err);
  }
};

const getCurrentUser = callApi(currentUserResourceDecoder, {});

interface Props extends RCProps<CurrentUserStore> {}

class CurrentUserReactions extends ReactionComponent<CurrentUserStore, State, Props> {
  tester = () => this.props.store.currentUserState;

  effect = (state: State) => {
    const { store } = this.props;

    switch (state.kind) {
      case 'waiting':
        break;
      case 'identifying':
        rootResourceStore
          .findLinkT('current-user')
          .andThen(getCurrentUser)
          .fork(handleLoginFailure(store), store.setCurrentUser);
        break;
      case 'identified':
        InactivityLogoutTracker.reconfig({
          timeLimit: minutes(state.currentUserResource.payload.inactivityTimeoutMinutes),
        });
        break;
      case 'logging-out':
        Task.succeed<MissingLinkError | AppyError, ReadonlyArray<Link>>(
          state.currentUserResource.links,
        )
          .andThen(findLinkT('logout'))
          .andThen((link) => retryOnSessionExpiration(request(link, {})))
          .fork(
            (err) => log('Session logout failed:', err),
            () => sessionStore.requesting({ messageOnFailure: nothing() }),
          );
        break;
      case 'anonymous':
      case 'unauthorized':
      case 'errored':
        discardSession();
        break;
      default:
        assertNever(state);
    }
  };
}

export default CurrentUserReactions;
